import { memo, useCallback, useState, useEffect } from "react";
import Form from "../../components/Form";
import Table from "../../components/Table";
import { getOwners, getAvailableAnimalsSale } from "../../api/index.js";
import { useSnackbar } from "../../utils/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import useListStyles from "../../components/styles/List.js";
import Spinner from "../../components/Spinner.js";
import { Typography, Box, Grid2, Button } from "@mui/material";
import {
    femaleAnimalHeader,
    maleAnimalHeader,
    lambAnimalCodeHeader,
    breedCodeHeader,
    birthDateHeader,
    sireCodeHeaderNonClick,
    damCodeHeaderNonClick,
    lambingDateHeader
} from "../../components/TableHeaders.js";
import AvailableAnimalsPdf from "./AvailableAnimalsPdf.js";
import AvailableLambsPdf from "./AvailableLambsPdf.js";

const typeTranslates = [
    { en: "maleAnimal", gr: "Διαθέσιμοι Κριοί" },
    { en: "femaleAnimal", gr: "Διαθέσιμες Προβατίνες" },
    { en: "lamb", gr: "Διαθέσιμοι Αμνοί" },
];

const AnimalsToSale = () => {
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [selectedTypeGr, setSelectedTypeGr] = useState();
    const [selectedOwner, setSelectedOwner] = useState();
    const [availableAnimals, setAvailableAnimals] = useState([]);
    const [animalsTableHeaders, setAnimalsTableHeaders] = useState([
        breedCodeHeader,
        birthDateHeader,
        sireCodeHeaderNonClick,
        damCodeHeaderNonClick,
    ])
    const lambsTableHeaders = [
        lambAnimalCodeHeader,
        breedCodeHeader,
        lambingDateHeader,
        sireCodeHeaderNonClick,
        damCodeHeaderNonClick,
    ]

    const selectClasses = useSelectStyles();
    const listClasses = useListStyles();
    const { error } = useSnackbar();

    const fetchData = useCallback(
        async () => {
            setIsLoading(true)
            try {
                const { owners } = await getOwners();
                setFetchedOwners(owners);
                const dropdownValues = owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                }));
                setOwnersDropdownValues(dropdownValues);
            } catch (err) {
                error(err);
            }
            setIsLoading(false);
        }
    )

    useEffect(() => {
        fetchData();
    }, []);

    const selectOwnerTypeFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "dropdown",
            id: "type",
            items: [
                { value: "Διαθέσιμοι Κριοί", text: "Διαθέσιμοι Κριοί" },
                { value: "Διαθέσιμες Προβατίνες", text: "Διαθέσιμες Προβατίνες" },
                { value: "Διαθέσιμοι Αμνοί", text: "Διαθέσιμοι Αμνοί" },
            ],
            placeholder: "Τύπος",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επόμενο",
        },
    ]

    const handleOwnerSelect = async (values) => {
        setIsLoading(true);
        setSelectedOwner(values.owner);
        const selOwner = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner);
        setSelectedTypeGr(values.type);
        const selType = typeTranslates.find((translate) => translate.gr === values.type)?.en;
        const { success: scs, animals, message } = await getAvailableAnimalsSale(selOwner._id, selType);
        if (scs) {
            setAvailableAnimals(animals);
            var newHeader
            if (selType === "maleAnimal") {
                newHeader = maleAnimalHeader;
            }
            if (selType === "femaleAnimal") {
                newHeader = femaleAnimalHeader
            }
            setAnimalsTableHeaders((prevHeaders) => [
                newHeader,
                ...prevHeaders,
            ]);
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <Box className={selectClasses.outerBox}>
                    <Box container flexDirection="column" className={selectClasses.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            {"Επιλέξτε Εκτροφή και τον Τύπο του Ζώου"}
                        </Typography>
                        <Form
                            content={selectOwnerTypeFormContent}
                            onSubmit={handleOwnerSelect}
                            toResetForm={false}
                        />
                    </Box>
                </Box>
            )}
            {step === 2 && (
                <>
                    <Grid2 container className={listClasses.container} direction="column" sx={{ marginTop: "1rem", marginLeft: "3rem" }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: "90%", marginBottom: "1rem" }}>
                            <Typography variant="h6" className={listClasses.form_title}>
                                {`${selectedTypeGr} προς πώληση για την εκτροφή "${selectedOwner}"`}
                            </Typography>
                            <Box>
                                <Button variant="contained" sx={{ marginRight: "0.5rem", backgroundColor: "secondary.main" }} onClick={() => setStep(3)}>
                                    PDF
                                </Button>
                            </Box>
                        </Box>
                        <Table
                            data={availableAnimals}
                            noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν εγγραφές!"}
                            columns={selectedTypeGr === "Διαθέσιμοι Αμνοί" ? lambsTableHeaders : animalsTableHeaders}
                        />
                    </Grid2>
                </>
            )}
            {step === 3 && selectedTypeGr !== "Διαθέσιμοι Αμνοί" && (
                <AvailableAnimalsPdf
                    owner={selectedOwner}
                    availableAnimals={availableAnimals}
                    codeTitle={selectedTypeGr === "Διαθέσιμοι Κριοί" ? "Κωδικός Κριού" : "Κωδικός Προβατίνας"} />
            )}
            {step === 3 && selectedTypeGr === "Διαθέσιμοι Αμνοί" && (
                <AvailableLambsPdf
                    owner={selectedOwner}
                    availableAnimals={availableAnimals}
                />
            )}
        </>
    )
}

export default memo(AnimalsToSale);